import { motion } from 'framer-motion';
import { ArrowRight, ChevronRight, Eye, Minus } from 'lucide-react';
import InfoSpot from '@/components/InfoSpot';
import PortalShell from '@/components/PortalShell';
import AutoCursor from '@/components/AutoCursor';

function WizardStepper({ active }) {
  const steps = ['Basic Details', 'Assign Roles', 'Review'];
  return (
    <div className="flex items-center justify-center py-4 px-6 bg-[#FAFAFA] rounded-xl mb-5">
      {steps.map((s, i) => (
        <div key={s} className="flex items-center">
          <div className="flex flex-col items-center gap-1.5">
            <div className={`w-7 h-7 rounded-full flex items-center justify-center text-[11px] font-bold border-2 ${
              i < active ? 'bg-teal-600 border-teal-600 text-white' :
              i === active ? 'bg-white border-teal-600 text-teal-600' :
              'bg-white border-[#E5E7EB] text-[#737373]'
            }`}>{i < active ? '✓' : <span className="w-2 h-2 rounded-full bg-current" />}</div>
            <span className={`text-[12px] font-body ${i <= active ? 'font-medium text-[#0A0A0A]' : 'text-[#737373]'}`}>{s}</span>
          </div>
          {i < steps.length - 1 && <div className={`w-24 h-[2px] mx-2 mt-[-18px] ${i < active ? 'bg-teal-600' : 'bg-[#E5E7EB]'}`} />}
        </div>
      ))}
    </div>
  );
}

function RoleOption({ name, desc, selected, selectAt = null, permissions }) {
  const delay = selectAt ?? 0;
  const animatesSelect = selectAt !== null;
  return (
    <motion.div
      initial={{ opacity: 0, y: 6 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay: 0.3 }}
      className={`p-4 rounded-xl border transition-colors ${selected && animatesSelect ? 'border-teal-600 bg-teal-50/30' : 'border-[#E5E7EB] bg-white'}`}
    >
      <div className="flex items-center justify-between mb-2">
        <div className="flex items-center gap-2.5">
          <motion.div
            animate={animatesSelect ? {
              borderColor: ['#E5E7EB', '#0D9488'],
              boxShadow: ['0 0 0 0 rgba(13,148,136,0)', '0 0 0 6px rgba(13,148,136,0.3)', '0 0 0 0 rgba(13,148,136,0)'],
            } : {}}
            transition={{ duration: 0.8, delay, times: [0, 0.5, 1] }}
            className={`w-4 h-4 rounded-full border-2 flex items-center justify-center ${selected && animatesSelect ? 'border-teal-600' : 'border-[#E5E7EB]'}`}
          >
            {selected && animatesSelect && (
              <motion.div initial={{ scale: 0 }} animate={{ scale: 1 }} transition={{ delay: delay + 0.3, type: 'spring', stiffness: 400 }}
                className="w-2 h-2 rounded-full bg-teal-600" />
            )}
          </motion.div>
          <span className="font-body font-medium text-sm text-[#0A0A0A]">{name}</span>
        </div>
        <span className="text-[11px] font-body text-teal-600 flex items-center gap-1"><Eye size={12} /> View detailed permissions</span>
      </div>
      <p className="text-[12px] font-body text-[#737373] ml-6 mb-1">{desc}</p>
      {selected && animatesSelect && permissions && (
        <motion.div initial={{ opacity: 0, height: 0 }} animate={{ opacity: 1, height: 'auto' }} transition={{ delay: delay + 0.5 }} className="ml-6 mt-2 space-y-1.5">
          <div className="flex items-center gap-2 flex-wrap">
            <span className="px-2 py-0.5 rounded text-[11px] font-medium bg-emerald-50 text-emerald-600 border border-emerald-200">Full Access</span>
            <span className="text-[12px] font-body text-[#737373]">{permissions.full}</span>
          </div>
          {permissions.partial && (
            <div className="flex items-center gap-2 flex-wrap">
              <span className="px-2 py-0.5 rounded text-[11px] font-medium bg-amber-50 text-amber-600 border border-amber-200">Partial</span>
              <span className="text-[12px] font-body text-[#737373]">{permissions.partial}</span>
            </div>
          )}
        </motion.div>
      )}
    </motion.div>
  );
}

export default function UserFormRolesScene({ onNext }) {
  // Flow:
  //  0.4s  cursor hovers over title InfoSpot
  //  1.0s  Info auto-opens, explains facility-specific roles
  //  3.5s  Info closes; cursor moves to first admin role radio
  //  4.2s  clicks admin in Burjeel Al Nahyan
  //  5.5s  cursor moves down to user role in Mussafah
  //  6.2s  clicks provider user
  //  7.2s  cursor moves to CTA
  //  8.0s  CTA pulses and gets clicked
  const cursorPath = [
    { x: 310, y: 80,  delay: 0.8 },                       // hover title InfoSpot
    { x: 310, y: 80,  delay: 2.6 },                       // linger while open
    { x: 360, y: 310, delay: 1.2, click: true },          // select admin role (facility 1)
    { x: 360, y: 540, delay: 1.6, click: true },          // select user role (facility 2)
    { x: 560, y: 620, delay: 1.4, click: true },          // click Next
  ];

  return (
    <PortalShell
      activeItem="User"
      overlay={<AutoCursor targets={cursorPath} startDelay={0.6} />}
    >
      <div data-testid="scene-user-form-roles" className="px-8 pt-5 pb-8">
        <div className="max-w-3xl mx-auto">
          <div className="text-[12px] font-body text-[#737373] mb-3">
            Manage <ChevronRight size={12} className="inline" /> Users <ChevronRight size={12} className="inline" /> <span className="text-[#0A0A0A] font-medium">Add new user</span>
          </div>
          <div className="mb-4">
            <h2 className="font-heading font-bold text-2xl tracking-tight text-[#0A0A0A] flex items-center gap-2">
              Assign Roles
              <InfoSpot
                text="Each linked facility gets its own role. Ahmed can be a Provider Admin in Burjeel Al Nahyan and a regular Provider User in Mussafah. Permissions apply per-facility."
                position="right"
                color="blue"
                autoOpenAt={0.9}
                autoCloseAfter={3.0}
                glowAt={0.5}
              />
            </h2>
            <p className="mt-1 font-body text-sm text-[#737373]">Define roles per facility for this user</p>
          </div>

          <WizardStepper active={1} />

          <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.2 }}
            className="bg-white rounded-2xl border border-[#E5E7EB] shadow-sm mb-4 overflow-hidden">
            <div className="flex items-center justify-between px-4 py-3 border-b border-[#E5E7EB] bg-[#FAFAFA]">
              <span className="font-heading font-bold text-sm text-[#0A0A0A]">Burjeel Al Nahyan</span>
              <Minus size={16} className="text-[#737373]" />
            </div>
            <div className="p-4 space-y-3">
              <RoleOption
                name="Provider Facility Admin"
                desc="Full access to provider facility administration and management"
                selected
                selectAt={4.0}
                permissions={{ full: 'Resources · Contracts · User Management · Facility Settings', partial: 'Surveys · Reporting' }}
              />
              <RoleOption name="Provider Facility User" desc="Standard access to facility resources and contracts" selected={false} />
            </div>
          </motion.div>

          <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.3 }}
            className="bg-white rounded-2xl border border-[#E5E7EB] shadow-sm mb-4 overflow-hidden">
            <div className="flex items-center justify-between px-4 py-3 border-b border-[#E5E7EB] bg-[#FAFAFA]">
              <span className="font-heading font-bold text-sm text-[#0A0A0A]">Burjeel - Mussafah</span>
              <Minus size={16} className="text-[#737373]" />
            </div>
            <div className="p-4 space-y-3">
              <RoleOption name="Provider Facility Admin" desc="Full access to provider facility administration and management" selected={false} />
              <RoleOption
                name="Provider Facility User"
                desc="Standard access to facility resources and contracts"
                selected
                selectAt={5.6}
                permissions={{ full: 'Resources · Contracts' }}
              />
            </div>
          </motion.div>

          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 7.0 }} className="flex justify-center mt-5">
            <motion.button
              data-testid="wizard-next-roles"
              onClick={onNext}
              animate={{
                scale: [1, 1, 1.1, 0.97, 1.06, 1],
                boxShadow: [
                  '0 0 0 0 rgba(13,148,136,0)',
                  '0 0 0 0 rgba(13,148,136,0)',
                  '0 0 0 10px rgba(13,148,136,0.3)',
                  '0 0 0 0 rgba(13,148,136,0)',
                  '0 0 0 8px rgba(13,148,136,0.25)',
                  '0 0 0 0 rgba(13,148,136,0)',
                ],
              }}
              transition={{ duration: 2.0, delay: 7.2, times: [0, 0.2, 0.4, 0.6, 0.8, 1] }}
              className="flex items-center gap-2 px-8 py-2.5 rounded-xl bg-teal-600 text-white font-body font-bold text-sm"
            >
              Next <ArrowRight size={16} />
            </motion.button>
          </motion.div>
        </div>
      </div>
    </PortalShell>
  );
}
